/**
 * Pages blocks:
 * - pages/next — execution block, advance to next page
 * - pages/prev — execution block, go to previous page
 * - pages/getActiveIndex — data block, returns current page index
 * - pages/getPageCount — data block, returns number of pages
 *
 * All blocks resolve the target's Pages behavior. If no Pages behavior is
 * present, they fall back to raw SceneObject children (same as setActiveChild).
 */

import { FlowGraphBlock } from "@babylonjs/core/FlowGraph/flowGraphBlock";
import { FlowGraphExecutionBlockWithOutSignal } from "@babylonjs/core/FlowGraph/flowGraphExecutionBlockWithOutSignal";
import type { FlowGraphContext } from "@babylonjs/core/FlowGraph/flowGraphContext";
import type { FlowGraphSignalConnection } from "@babylonjs/core/FlowGraph/flowGraphSignalConnection";
import type { IFlowGraphBlockConfiguration } from "@babylonjs/core/FlowGraph/flowGraphBlock";
import { RichTypeNumber, RichTypeBoolean } from "@babylonjs/core/FlowGraph/flowGraphRichTypes";
import { resolveTarget } from './traceContext';
import { Pages } from '../../pages';

// ============================================================================
// pages/next, pages/prev — execution blocks (shared base)
// ============================================================================

class TracePagesCycleBlock extends FlowGraphExecutionBlockWithOutSignal {
    private _targetPath: string;
    private _direction: 1 | -1;

    constructor(direction: 1 | -1, config?: IFlowGraphBlockConfiguration & { target?: string }) {
        super(config);
        this._direction = direction;
        this._targetPath = config?.target ?? '';
        this.registerDataInput("wrap", RichTypeBoolean, true);
        this.registerDataInput("animate", RichTypeBoolean, false);
    }

    public override _execute(context: FlowGraphContext, _callingSignal: FlowGraphSignalConnection): void {
        const target = resolveTarget(context, this._targetPath);
        if (!target) {
            this.out._activateSignal(context);
            return;
        }

        const wrap = this.getDataInput("wrap")!.getValue(context) as boolean;
        const animate = this.getDataInput("animate")!.getValue(context) as boolean;
        const pages = target.getBehavior(Pages);

        if (pages) {
            // Fire-and-forget: page transition runs in background, flow continues immediately
            // (consistent with TracePlayAnimationBlock's out signal)
            void (this._direction === 1 ? pages.next(wrap, animate) : pages.prev(wrap, animate));
            this.out._activateSignal(context);
        } else {
            // Fallback: raw children
            const children = target.getChildren();
            if (children.length === 0) {
                this.out._activateSignal(context);
                return;
            }
            const currentIndex = children.findIndex(c => c.active);
            const idx = currentIndex === -1 ? 0 : currentIndex;
            let newIdx = idx + this._direction;
            if (newIdx >= children.length) newIdx = wrap ? 0 : children.length - 1;
            if (newIdx < 0) newIdx = wrap ? children.length - 1 : 0;
            for (let i = 0; i < children.length; i++) {
                children[i].setActive(i === newIdx);
            }
            this.out._activateSignal(context);
        }
    }
}

export class TracePagesNextBlock extends TracePagesCycleBlock {
    constructor(config?: IFlowGraphBlockConfiguration & { target?: string }) {
        super(1, config);
    }
    public override getClassName(): string { return "TracePagesNextBlock"; }
}

export class TracePagesPrevBlock extends TracePagesCycleBlock {
    constructor(config?: IFlowGraphBlockConfiguration & { target?: string }) {
        super(-1, config);
    }
    public override getClassName(): string { return "TracePagesPrevBlock"; }
}

// ============================================================================
// pages/getActiveIndex — data block
// ============================================================================

export class TracePagesGetActiveIndexBlock extends FlowGraphBlock {
    private _targetPath: string;

    constructor(config?: IFlowGraphBlockConfiguration & { target?: string }) {
        super(config);
        this._targetPath = config?.target ?? '';
        this.registerDataOutput("value", RichTypeNumber);
    }

    public override _updateOutputs(context: FlowGraphContext): void {
        const target = resolveTarget(context, this._targetPath);
        if (!target) {
            this.getDataOutput("value")!.setValue(0, context);
            return;
        }

        const pages = target.getBehavior(Pages);
        if (pages) {
            this.getDataOutput("value")!.setValue(pages.getActivePageIndex(), context);
        } else {
            // Fallback: find first active child
            const children = target.getChildren();
            const idx = children.findIndex(c => c.active);
            this.getDataOutput("value")!.setValue(idx === -1 ? 0 : idx, context);
        }
    }

    public override getClassName(): string {
        return "TracePagesGetActiveIndexBlock";
    }
}

// ============================================================================
// pages/getPageCount — data block
// ============================================================================

export class TracePagesGetPageCountBlock extends FlowGraphBlock {
    private _targetPath: string;

    constructor(config?: IFlowGraphBlockConfiguration & { target?: string }) {
        super(config);
        this._targetPath = config?.target ?? '';
        this.registerDataOutput("value", RichTypeNumber);
    }

    public override _updateOutputs(context: FlowGraphContext): void {
        const target = resolveTarget(context, this._targetPath);
        if (!target) {
            this.getDataOutput("value")!.setValue(0, context);
            return;
        }

        const pages = target.getBehavior(Pages);
        if (pages) {
            this.getDataOutput("value")!.setValue(pages.getPageCount(), context);
        } else {
            this.getDataOutput("value")!.setValue(target.getChildren().length, context);
        }
    }

    public override getClassName(): string {
        return "TracePagesGetPageCountBlock";
    }
}
